import { useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { usePortalLayout } from '../../../components/common/PortalLayout'
import { useTheme } from '../../../components/common/Theme/ThemeContext'
import {
  buildOrchestrationFoSrc,
  getOrchestrationFoBaseUrl,
  getOrchestrationFoOrigin,
  normalizeFoPath,
  PORTAL_ORCHESTRATION_PREFIX,
  toFoThemeQuery,
  withFoThemeSearch,
} from './orchestrationFoUrl'
import './Workflows.css'

const FO_ROUTE_SYNC_SOURCE = 'selfic-orchestration-fo'

function foPathFromPortalLocation(pathname: string): string {
  if (!pathname.startsWith(PORTAL_ORCHESTRATION_PREFIX)) return '/workflows'
  const rest = pathname.slice(PORTAL_ORCHESTRATION_PREFIX.length)
  return normalizeFoPath(rest || '/workflows')
}

/**
 * Embed FO standalone SPA in an iframe.
 * JWT + tenant go in the URL hash (not query) so they are not logged by IIS/CDN.
 * Theme goes in the query string.
 * Nested FO routes are mirrored to /portal/orchestration{path} via postMessage.
 *
 * Set REACT_APP_ORCHESTRATION_FO_URL (e.g. http://localhost:5173).
 */
export default function OrchestrationV2() {
  const { theme } = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const { setSidebarCollapsed } = usePortalLayout()
  const foBase = getOrchestrationFoBaseUrl()
  const foOrigin = getOrchestrationFoOrigin()

  const initialFoPath = useMemo(
    () => foPathFromPortalLocation(location.pathname),
    // Seed iframe once from the URL at mount (deep link).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // Stable first src so in-iframe navigation does not remount / re-auth.
  // Theme changes remount intentionally (new query + hash).
  const srcRef = useRef<string | null>(null)
  const themeForSrcRef = useRef(theme)
  if (srcRef.current === null || themeForSrcRef.current !== theme) {
    themeForSrcRef.current = theme
    srcRef.current = buildOrchestrationFoSrc(foBase, theme, initialFoPath)
  }
  const src = srcRef.current
  const foTheme = toFoThemeQuery(theme)

  useEffect(() => {
    setSidebarCollapsed(true)
  }, [setSidebarCollapsed])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('theme') === foTheme) return
    navigate(`${location.pathname}${withFoThemeSearch(theme, location.search)}`, { replace: true })
  }, [foTheme, location.pathname, location.search, navigate, theme])

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== foOrigin) return
      const data = event.data
      if (!data || typeof data !== 'object') return
      if (data.source !== FO_ROUTE_SYNC_SOURCE || data.type !== 'route') return
      if (typeof data.path !== 'string') return

      const foPath = normalizeFoPath(data.path.split('?')[0] || '/workflows')
      const incomingSearch = data.path.includes('?')
        ? data.path.slice(data.path.indexOf('?'))
        : ''
      const search = withFoThemeSearch(theme, incomingSearch)
      const next = `${PORTAL_ORCHESTRATION_PREFIX}${foPath}${search}`
      const current = `${location.pathname}${location.search}`
      if (next !== current) {
        navigate(next, { replace: true })
      }
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [foOrigin, location.pathname, location.search, navigate, theme])

  return (
    <div className="wf-v2">
      <iframe
        className="wf-v2__frame"
        title="Orchestration"
        src={src}
        style={{ background: theme === 'black' ? '#0a0a0a' : '#fff' }}
        allow="clipboard-read; clipboard-write"
      />
    </div>
  )
}
